import { ROUTE, type RouteValue } from "../constants/routes";
import type { BooleanFeatureKey } from "../types/subscription.types";

export type RoutePermissionMode = "any" | "all";

export interface RoutePermissionRule {
  permissions: string[];
  mode?: RoutePermissionMode;
  allowWithoutTerreiro?: boolean;
  allowWaitingApproval?: boolean;
  requiredFeature?: BooleanFeatureKey;
  notes?: string;
}

/**
 * Phase 0 (migration prep): canonical role->permission mapping for route guards.
 *
 * Goal: replace `RoleProtectedPage allowedRoles=[...]` with
 * `PermissionProtectedPage requiredPermissions=[...]` in AppRouter.
 *
 * Source of permission codes:
 * - ilovemacumba-api/src/prisma/seeds/seed.permission.ts
 * - ilovemacumba-api/docs/ROLE_TO_PERMISSION_MAPPING.md
 */
export const PERMISSION_ROUTE_MATRIX: Partial<
  Record<RouteValue | string, RoutePermissionRule>
> = {
  // Activities (A-Z)
  [ROUTE.ACTIVITIES]: {
    permissions: ["activity.view", "activity.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.ACTIVITIES_NEW]: {
    permissions: ["activity.create", "activity.create.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.ACTIVITY_EDIT]: {
    permissions: ["activity.update", "activity.update.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.ACTIVITY_USE_MODEL]: {
    permissions: ["activity.create", "activity.create.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUALS]: {
    permissions: ["activity.view", "activity.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUALS_NEW]: {
    permissions: ["activity.create", "activity.create.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUAL_EDIT]: {
    permissions: ["activity.update", "activity.update.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUAL_USE_MODEL]: {
    permissions: ["activity.create", "activity.create.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },

  // Dashboard / badges (A-Z)
  [ROUTE.BADGES]: { permissions: ["settings.view"] },
  [ROUTE.ACADEMY]: {
    permissions: ["academy.view"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.ACADEMY_MODULE]: {
    permissions: ["academy.view"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.ATTENDANCE_INSIGHTS]: { permissions: ["report.view_members"] },
  [ROUTE.MEDIUMS_ATTENDANCE]: { permissions: ["report.view_members"] },

  // Development-only routes (A-Z)
  "/dev/subscription-test": {
    permissions: ["admin.full_access"],
    notes: "DEV-only route",
  },

  // Events (A-Z)
  [ROUTE.EVENTS]: { permissions: ["event.view"] },
  [ROUTE.EVENTS_NEW]: { permissions: ["event.create"] },
  [ROUTE.EVENT_TICKETS]: {
    permissions: ["event.publish", "event.ticketing.view_holders"],
    mode: "any",
  },
  [ROUTE.INGRESSOS_NEW]: { permissions: ["event.publish"] },

  // Financial (A-Z)
  [ROUTE.FINANCIAL]: {
    permissions: [
      "financial.view_dashboard",
      "financial.view_dashboard.own",
      "financial.transparency.read",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_ACCOUNTS]: {
    permissions: [
      "financial.manage_accounts",
      "financial.manage_accounts_update.own",
      "financial.manage_accounts_view.own",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_MONTHLY_FEE]: {
    permissions: [
      "financial.view_dashboard",
      "financial.view_dashboard.own",
      "financial.transparency.read",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_ACCOUNT]: {
    permissions: [
      "financial.manage_accounts",
      "financial.manage_accounts_update.own",
      "financial.manage_accounts_view.own",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_INCOME]: {
    permissions: [
      "financial.view_transactions",
      "financial.view_transactions.own",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_INCOME_EDIT]: {
    permissions: ["financial.update_income", "financial.update_incomes.own"],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_MONTHLY_FEE_EDIT]: {
    permissions: ["financial.manage_accounts"],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_MONTHLY_FEE_PAYMENTS]: {
    permissions: [
      "financial.monthly_fee.view_requests",
      "financial.monthly_fee.review",
    ],
    mode: "all",
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_NEW]: {
    permissions: [
      "financial.manage_accounts",
      "financial.manage_accounts_create.own",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_OUTCOME]: {
    permissions: [
      "financial.view_transactions",
      "financial.view_transactions.own",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_OUTCOME_EDIT]: {
    permissions: ["financial.update_outcome", "financial.update_outcomes.own"],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
  [ROUTE.FINANCIAL_TRANSPARENCY]: {
    permissions: [
      "financial.transparency.read",
      "financial.monthly_fee.submit_receipt",
    ],
    mode: "any",
    requiredFeature: "canManageFinancials",
  },

  // Inventory (A-Z)
  [ROUTE.INVENTORY]: {
    permissions: ["inventory.view", "inventory.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.INVENTORY_LIST]: {
    permissions: ["inventory.view", "inventory.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.INVENTORY_SUPPLY]: {
    permissions: ["inventory.view", "inventory.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.INVENTORY_CATEGORIES]: {
    permissions: ["inventory.view", "inventory.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.INVENTORY_EDIT]: {
    permissions: ["inventory.update", "inventory.update.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.INVENTORY_NEW]: {
    permissions: ["inventory.create", "inventory.create.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.INVENTORY_VIEW]: {
    permissions: ["inventory.view", "inventory.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.SUPPLY_PLAN]: {
    permissions: ["inventory.view", "inventory.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },

  // Leaderboard (A-Z)
  [ROUTE.LEADERBOARD]: { permissions: ["settings.view"] },
  [ROUTE.GAMIFICATION_HELP]: { permissions: ["settings.view"] },

  // Mediums (A-Z)
  [ROUTE.MEDIUM]: { permissions: ["member.view"] },
  [ROUTE.MEDIUMS]: { permissions: ["member.view"] },
  [ROUTE.MEDIUMS_LIST]: { permissions: ["member.view"] },
  [ROUTE.MEDIUMS_RANKS]: { permissions: ["member.view"] },
  [ROUTE.MEDIUMS_LINES]: {
    permissions: ["spiritual_profile.view_analytics"],
  },
  [ROUTE.MEDIUMS_NEW]: { permissions: ["member.invite"] },

  // Profile (A-Z)
  [ROUTE.PROFILE]: {
    permissions: ["member.view_profile"],
    allowWithoutTerreiro: true,
    allowWaitingApproval: true,
    notes:
      "Only profile root remains accessible while WAITING_APPROVAL so users can complete requested changes",
  },
  [ROUTE.PROFILE_ADDRESS]: {
    permissions: ["settings.update_address"],
    allowWithoutTerreiro: true,
    notes:
      "Personal profile address must be accessible without terreiro context; backend enforces owner scope",
  },
  [ROUTE.PROFILE_ADDRESS_WITH_ID]: {
    permissions: ["settings.update_address"],
    allowWithoutTerreiro: true,
    notes:
      "Personal profile address must be accessible without terreiro context; backend enforces owner scope",
  },
  [ROUTE.PROFILE_CONTACTS]: {
    permissions: ["settings.update_contacts"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.PROFILE_CREDENTIALS]: {
    permissions: ["settings.update_profile"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.PROFILE_PASSKEYS]: {
    permissions: ["settings.update_profile"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.PROFILE_DATA]: {
    permissions: ["settings.update_profile"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.PROFILE_MEDIUM]: {
    permissions: ["member.view_profile"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.PROFILE_MEDIUM_DATA]: {
    permissions: ["member.view_profile"],
    allowWithoutTerreiro: true,
  },
  [ROUTE.SPIRITUAL_PROFILE]: {
    permissions: ["spiritual_profile.view_self"],
    allowWithoutTerreiro: true,
    notes:
      "Supports owner-scope resolution via mediumId for profile contexts without active terreiro selection",
  },
  [ROUTE.SPIRITUAL_PROFILE_NEW]: {
    permissions: ["spiritual_profile.view_self"],
    allowWithoutTerreiro: true,
    notes:
      "Fullscreen spiritual form chat; create is gated by spiritual_profile.manage_self in the assistant provider",
  },

  // Ritual baths (A-Z)
  [ROUTE.RITUAL_BATH]: {
    permissions: ["ritual_bath.view", "ritual_bath.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUAL_BATH_CATEGORIES]: {
    permissions: ["ritual_bath.manage_inventory"],
  },
  [ROUTE.RITUAL_BATH_EDIT]: {
    permissions: ["ritual_bath.update", "ritual_bath.update.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUAL_BATH_SCHEDULE]: {
    permissions: ["ritual_bath.update", "ritual_bath.update.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUAL_BATHS]: {
    permissions: ["ritual_bath.view", "ritual_bath.view.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },
  [ROUTE.RITUAL_BATHS_NEW]: {
    permissions: ["ritual_bath.create", "ritual_bath.create.own"],
    mode: "any",
    allowWithoutTerreiro: true,
  },

  // Settings and admin (A-Z)
  [ROUTE.ROLE_CREATE]: { permissions: ["admin.manage_roles"] },
  [ROUTE.ROLE_EDIT]: { permissions: ["admin.manage_roles"] },
  [ROUTE.ROLE_MANAGEMENT]: { permissions: ["admin.manage_roles"] },
  [ROUTE.SETTINGS]: {
    permissions: ["settings.view"],
    allowWithoutTerreiro: true,
    allowWaitingApproval: true,
    notes:
      "Settings overview remains accessible while WAITING_APPROVAL so terreiro members can review configuration and self-service information before approval",
  },
  [ROUTE.SETTINGS_MEETINGS]: {
    permissions: ["settings.view"],
    allowWithoutTerreiro: true,
    allowWaitingApproval: true,
    notes:
      "Meeting scheduling page follows settings overview access for personal profile and waiting-approval flows",
  },
  [ROUTE.SETTINGS_USAGE]: {
    permissions: ["settings.manage_subscription"],
    allowWithoutTerreiro: true,
    notes:
      "Personal profile skips terreiro permission checks; terreiro requires settings.manage_subscription",
  },
  [ROUTE.SETTINGS_PLAN]: {
    permissions: ["settings.manage_subscription"],
    allowWithoutTerreiro: true,
    notes:
      "Personal profile skips terreiro permission checks; terreiro requires settings.manage_subscription",
  },
  [ROUTE.SETTINGS_NOTIFICATIONS]: {
    permissions: ["settings.view"],
    allowWithoutTerreiro: true,
    allowWaitingApproval: true,
    notes:
      "Notification preferences are user-scoped and follow settings overview access",
  },
  [ROUTE.SETTINGS_PRIVACY_POLICY]: {
    permissions: ["terreiro.manage_policy"],
    notes: "Terreiro policy editor requires manage_policy in an active terreiro",
  },
  [ROUTE.AFFILIATE_PROGRAM]: {
    permissions: ["settings.view"],
    allowWithoutTerreiro: true,
  },

  // Subscription (A-Z)
  [ROUTE.SUBSCRIPTION_CANCELATION]: {
    permissions: ["settings.manage_subscription"],
  },
  [ROUTE.SUBSCRIPTION_REQUIRED]: {
    permissions: ["settings.view"],
    notes:
      "Historically medium-only by role id; mapped to a minimal authenticated terreiro gate",
  },
  [ROUTE.SUBSCRIPTION_SUCCESSFUL]: {
    permissions: ["settings.manage_subscription"],
  },

  // Terreiro (A-Z)
  [ROUTE.TERREIRO]: {
    permissions: [
      "terreiro.view_as_medium",
      "terreiro.view_as_admin",
      "terreiro.view_as_personal_profile",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    notes:
      "Terreiro base route supports readonly medium/admin profile views and personal-profile context",
  },
  [ROUTE.TERREIRO_ADDRESS]: { permissions: ["terreiro.address.update"] },
  [ROUTE.TERREIRO_CONTACT]: { permissions: ["terreiro.contact.manage"] },
  [ROUTE.TERREIRO_JOIN]: {
    permissions: ["member.invite"],
    allowWithoutTerreiro: true,
    notes:
      "Join flow must stay accessible without active terreiro context; backend enforces invite constraints",
  },
  [ROUTE.TERREIRO_MEDIUM_VIEW]: { permissions: ["member.view_profile"] },
  [ROUTE.TERREIRO_PROFILE]: { permissions: ["terreiro.info.update"] },

  // Transactions (A-Z)
  [ROUTE.TRANSACTIONS_NEW]: {
    permissions: [
      "financial.create_income",
      "financial.create_expense",
      "financial.create_incomes.own",
      "financial.create_outcomes.own",
    ],
    mode: "any",
    allowWithoutTerreiro: true,
    requiredFeature: "canManageFinancials",
  },
};
